import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const AiMatchProfile = () => {    
    return (
        <div className="container mx-auto px-4 md:px-6 pt-24 pb-16">
            {/* SEO Settings */}
            <title>{`AI Profile Matching – Discover Jobs That Fit Your Skills`}</title>
            <meta
                name="description"
                content="Use AI-powered profile matching on Hunar Pool to discover job opportunities that align with your skills, experience, and career goals."
            />

            {/* Hero Section */}
            <div className="text-center mb-16">
                <div className="inline-flex items-center justify-center px-4 py-2 rounded-full bg-gradient-to-r from-[#0096C7]/20 to-[#4361EE]/20 border border-[#0096C7]/30 mb-6">
                <span className="text-[#0096C7] font-medium text-sm">
                    <i className="fas fa-bolt mr-2" /> COMING SOON
                </span>
                </div>
                <h1 className="text-4xl md:text-6xl font-bold mb-6">
                    AI Match <span className="gradient-text">Profile</span>
                </h1>
                <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
                    We're building a revolutionary AI-powered profile that intelligently
                    matches your unique skills, experience, and aspirations with perfect
                    career opportunities.
                </p>                
            </div>

            {/* Features Preview */}
            <div className="mb-16">
                <h2 className="text-3xl font-bold text-center mb-12">
                    What to Expect from Your AI Match Profile
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {/* Feature 1 */}
                <div className="card-hover section-bg rounded-2xl p-6 border border-gray-800">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#0096C7]/20 to-[#0096C7]/10 flex items-center justify-center mb-6">
                    <i className="fas fa-brain text-2xl text-[#0096C7]" />
                    </div>
                    <h3 className="text-xl font-bold mb-4">Smart Skill Analysis</h3>
                    <p className="text-gray-300 mb-4">
                        Our AI will analyze your skills, experience, and achievements to build
                        a comprehensive understanding of your professional capabilities.
                    </p>
                    <div className="flex items-center text-sm text-gray-400">
                    <i
                        className="fas fa-cog animate-spin mr-2"
                        style={{ animationDuration: "3s" }}
                    />
                    <span>In Development</span>
                    </div>
                </div>
                {/* Feature 2 */}
                <div className="card-hover section-bg rounded-2xl p-6 border border-gray-800">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#4361EE]/20 to-[#4361EE]/10 flex items-center justify-center mb-6">
                    <i className="fas fa-bullseye text-2xl text-[#4361EE]" />
                    </div>
                    <h3 className="text-xl font-bold mb-4">Precision Job Matching</h3>
                    <p className="text-gray-300 mb-4">
                        Get matched with opportunities that truly fit your skills,
                        preferences, and career aspirations—not just keyword matches.
                    </p>
                    <div className="flex items-center text-sm text-gray-400">
                    <i
                        className="fas fa-cog animate-spin mr-2"
                        style={{ animationDuration: "3s" }}
                    />
                    <span>In Development</span>
                    </div>
                </div>
                {/* Feature 3 */}
                <div className="card-hover section-bg rounded-2xl p-6 border border-gray-800">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#06D6A0]/20 to-[#06D6A0]/10 flex items-center justify-center mb-6">
                    <i className="fas fa-chart-line text-2xl text-[#06D6A0]" />
                    </div>
                    <h3 className="text-xl font-bold mb-4">Career Growth Insights</h3>
                    <p className="text-gray-300 mb-4">
                        Receive personalized recommendations for skills to develop and career
                        paths to explore based on market trends.
                    </p>
                    <div className="flex items-center text-sm text-gray-400">
                    <i
                        className="fas fa-cog animate-spin mr-2"
                        style={{ animationDuration: "3s" }}
                    />
                    <span>In Development</span>
                    </div>
                </div>
                {/* Feature 4 */}
                <div className="card-hover section-bg rounded-2xl p-6 border border-gray-800">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#FFD166]/20 to-[#FFD166]/10 flex items-center justify-center mb-6">
                    <i className="fas fa-user-check text-2xl text-[#FFD166]" />
                    </div>
                    <h3 className="text-xl font-bold mb-4">Cultural Fit Scoring</h3>
                    <p className="text-gray-300 mb-4">
                        AI will analyze company culture and team dynamics to match you with
                        environments where you'll thrive.
                    </p>
                    <div className="flex items-center text-sm text-gray-400">
                    <i
                        className="fas fa-cog animate-spin mr-2"
                        style={{ animationDuration: "3s" }}
                    />
                    <span>In Development</span>
                    </div>
                </div>
                {/* Feature 5 */}
                <div className="card-hover section-bg rounded-2xl p-6 border border-gray-800">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#EF476F]/20 to-[#EF476F]/10 flex items-center justify-center mb-6">
                    <i className="fas fa-bolt text-2xl text-[#EF476F]" />
                    </div>
                    <h3 className="text-xl font-bold mb-4">Real-time Opportunity Alerts</h3>
                    <p className="text-gray-300 mb-4">
                        Get instant notifications when perfect matches become available,
                        giving you a competitive advantage.
                    </p>
                    <div className="flex items-center text-sm text-gray-400">
                    <i
                        className="fas fa-cog animate-spin mr-2"
                        style={{ animationDuration: "3s" }}
                    />
                    <span>In Development</span>
                    </div>
                </div>
                {/* Feature 6 */}
                <div className="card-hover section-bg rounded-2xl p-6 border border-gray-800">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#9D4EDD]/20 to-[#9D4EDD]/10 flex items-center justify-center mb-6">
                    <i className="fas fa-shield-alt text-2xl text-[#9D4EDD]" />
                    </div>
                    <h3 className="text-xl font-bold mb-4">Privacy-First Design</h3>
                    <p className="text-gray-300 mb-4">
                        Your data is protected with enterprise-grade security. You control
                        what information is shared with employers.
                    </p>
                    <div className="flex items-center text-sm text-gray-400">
                    <i
                        className="fas fa-cog animate-spin mr-2"
                        style={{ animationDuration: "3s" }}
                    />
                    <span>In Development</span>
                    </div>
                </div>
                </div>
            </div>
        </div>
    );
};


export default AiMatchProfile;